import { getComponent, isComponentAvailable } from "../data/catalog";
import { getMap, getRack } from "../data/maps";
import { getScenario } from "../data/scenarios";
import {
    createId,
    createPlacement,
    findFirstFreeStartU,
    rackContentsFitSize,
    validatePlacement,
} from "../domain/placement";
import { applyRackSettingsToMap, findRackSettings, isRackSizeConfigurable } from "../domain/rack-settings";
import type {
    AppView,
    MapDefinition,
    MapId,
    Placement,
    Plan,
    RackSettings,
    ScenarioId,
    WorkspaceSnapshot,
} from "../domain/types";

const HISTORY_LIMIT = 100;

export interface PlannerState extends WorkspaceSnapshot {
    readonly past: readonly WorkspaceSnapshot[];
    readonly future: readonly WorkspaceSnapshot[];
    readonly exportedUpdatedAt: string | null;
    readonly notice: string | null;
}

export type PlannerAction =
    | { readonly type: "new-plan"; readonly name: string; readonly mapId: MapId; readonly scenarioId: ScenarioId }
    | { readonly type: "load-plan"; readonly plan: Plan; readonly notice?: string }
    | { readonly type: "rename-plan"; readonly name: string }
    | { readonly type: "set-scenario"; readonly scenarioId: ScenarioId }
    | {
          readonly type: "configure-rack";
          readonly rackId: string;
          readonly name: string;
          readonly showNameAsComponentPrefix: boolean;
          readonly sizeU: number;
      }
    | { readonly type: "reset-plan" }
    | { readonly type: "select-rack"; readonly rackId: string; readonly openDesigner?: boolean }
    | { readonly type: "set-view"; readonly view: AppView }
    | {
          readonly type: "place";
          readonly componentId: string;
          readonly rackId: string;
          readonly startU: number;
          readonly localName?: string;
      }
    | {
          readonly type: "place-many";
          readonly placements: readonly {
              readonly componentId: string;
              readonly rackId: string;
              readonly startU: number;
              readonly localName?: string;
          }[];
      }
    | { readonly type: "move"; readonly placementId: string; readonly rackId: string; readonly startU: number }
    | {
          readonly type: "move-many";
          readonly placements: readonly {
              readonly placementId: string;
              readonly rackId: string;
              readonly startU: number;
          }[];
      }
    | { readonly type: "rename-placement"; readonly placementId: string; readonly localName: string }
    | { readonly type: "duplicate"; readonly placementId: string }
    | { readonly type: "delete"; readonly placementId: string }
    | { readonly type: "delete-many"; readonly placementIds: readonly string[] }
    | { readonly type: "undo" }
    | { readonly type: "redo" }
    | { readonly type: "mark-exported"; readonly updatedAt: string }
    | { readonly type: "notice"; readonly notice: string | null };

export const INITIAL_STATE: PlannerState = {
    plan: null,
    selectedRackId: null,
    view: "project",
    past: [],
    future: [],
    exportedUpdatedAt: null,
    notice: null,
};

function snapshot(state: PlannerState): WorkspaceSnapshot {
    return { plan: state.plan, selectedRackId: state.selectedRackId, view: state.view };
}

function commit(state: PlannerState, next: WorkspaceSnapshot, notice: string | null = null): PlannerState {
    const nextPast = [...state.past.slice(-(HISTORY_LIMIT - 1)), snapshot(state)];
    return { ...state, ...next, past: nextPast, future: [], notice };
}

function updatePlan(state: PlannerState, placements: readonly Placement[], notice: string): PlannerState {
    if (state.plan === null) {
        return { ...state, notice: "Create or import a plan first." };
    }
    const plan: Plan = {
        ...state.plan,
        project: { ...state.plan.project, updatedAt: new Date().toISOString() },
        placements,
    };
    return commit(state, { ...snapshot(state), plan }, notice);
}

function getConfiguredPlanMap(plan: Plan): MapDefinition | undefined {
    const map = getMap(plan.project.mapId);
    return map === undefined ? undefined : applyRackSettingsToMap(map, plan.rackSettings);
}

function configureRack(state: PlannerState, action: Extract<PlannerAction, { type: "configure-rack" }>): PlannerState {
    if (state.plan === null) {
        return { ...state, notice: "Create or import a plan first." };
    }
    const map = getMap(state.plan.project.mapId);
    const rack = map === undefined ? undefined : getRack(map, action.rackId);
    const currentSetting = findRackSettings(state.plan.rackSettings, action.rackId);
    const name = action.name.trim();
    if (rack === undefined) {
        return { ...state, notice: "The selected rack is unavailable." };
    }
    if (name === "" || action.name.length > 80) {
        return { ...state, notice: "Rack names must contain between 1 and 80 characters." };
    }
    const sizeConfigurable = isRackSizeConfigurable(rack, currentSetting);
    const currentSizeU = currentSetting?.sizeU ?? rack.sizeU;
    const sizeAllowed = sizeConfigurable ? action.sizeU === 24 || action.sizeU === 42 : action.sizeU === currentSizeU;
    if (!sizeAllowed) {
        return { ...state, notice: "This rack height is supplied by the imported data and cannot be changed." };
    }
    if (!rackContentsFitSize(rack, action.sizeU, state.plan.placements)) {
        return { ...state, notice: "Rack equipment does not fit within the selected height." };
    }

    const customName = name === rack.name ? undefined : name;
    const sizeU =
        sizeConfigurable && action.sizeU !== rack.sizeU
            ? (action.sizeU as 24 | 42)
            : currentSetting?.sizeSource === undefined
              ? undefined
              : currentSetting.sizeU;
    const nextSetting: RackSettings = {
        rackId: rack.id,
        ...(customName === undefined ? {} : { name: customName }),
        showNameAsComponentPrefix: action.showNameAsComponentPrefix,
        ...(sizeU === undefined ? {} : { sizeU }),
        ...(currentSetting?.sizeSource === undefined ? {} : { sizeSource: currentSetting.sizeSource }),
    };
    const rackSettings = state.plan.rackSettings.filter((item) => item.rackId !== rack.id);
    if (
        customName !== undefined ||
        action.showNameAsComponentPrefix ||
        nextSetting.sizeU !== undefined ||
        nextSetting.sizeSource !== undefined
    ) {
        rackSettings.push(nextSetting);
    }
    const plan: Plan = {
        ...state.plan,
        project: { ...state.plan.project, updatedAt: new Date().toISOString() },
        rackSettings,
    };
    return commit(state, { ...snapshot(state), plan }, "Rack settings updated.");
}

function placeComponent(state: PlannerState, action: Extract<PlannerAction, { type: "place" }>): PlannerState {
    if (state.plan === null) {
        return { ...state, notice: "Create or import a plan first." };
    }
    const map = getConfiguredPlanMap(state.plan);
    const component = getComponent(action.componentId);
    if (
        map === undefined ||
        component === undefined ||
        !isComponentAvailable(component, state.plan.project.scenarioId, state.plan.project.mapId)
    ) {
        return { ...state, notice: "The selected map or component is unavailable." };
    }
    const result = validatePlacement(map, state.plan.placements, component.id, action.rackId, action.startU);
    if (!result.valid) {
        return { ...state, notice: result.reason ?? "This placement is not valid." };
    }
    const placement = createPlacement(component, action.rackId, action.startU, action.localName);
    return updatePlan(state, [...state.plan.placements, placement], `${component.name} placed at U${action.startU}.`);
}

function placeComponents(state: PlannerState, action: Extract<PlannerAction, { type: "place-many" }>): PlannerState {
    if (state.plan === null || action.placements.length === 0) {
        return state;
    }
    const map = getConfiguredPlanMap(state.plan);
    if (map === undefined) {
        return { ...state, notice: "The selected map is unavailable." };
    }

    const nextPlacements = [...state.plan.placements];
    for (const draft of action.placements) {
        const component = getComponent(draft.componentId);
        if (
            component === undefined ||
            !isComponentAvailable(component, state.plan.project.scenarioId, state.plan.project.mapId)
        ) {
            return { ...state, notice: "One of the copied components is unavailable." };
        }
        const result = validatePlacement(map, nextPlacements, component.id, draft.rackId, draft.startU);
        if (!result.valid) {
            return { ...state, notice: result.reason ?? "The copied selection cannot be placed here." };
        }
        nextPlacements.push(createPlacement(component, draft.rackId, draft.startU, draft.localName));
    }

    return updatePlan(
        state,
        nextPlacements,
        `${action.placements.length} component${action.placements.length === 1 ? "" : "s"} pasted.`,
    );
}

function moveComponent(state: PlannerState, action: Extract<PlannerAction, { type: "move" }>): PlannerState {
    if (state.plan === null) {
        return state;
    }
    const placement = state.plan.placements.find((item) => item.id === action.placementId);
    const map = getConfiguredPlanMap(state.plan);
    if (placement === undefined || map === undefined) {
        return { ...state, notice: "The component can no longer be found." };
    }
    const result = validatePlacement(
        map,
        state.plan.placements,
        placement.componentId,
        action.rackId,
        action.startU,
        placement.id,
    );
    if (!result.valid) {
        return { ...state, notice: result.reason ?? "This placement is not valid." };
    }
    const placements = state.plan.placements.map((item) =>
        item.id === placement.id ? { ...item, rackId: action.rackId, startU: action.startU } : item,
    );
    return updatePlan(state, placements, `Component moved to U${action.startU}.`);
}

function moveComponents(state: PlannerState, action: Extract<PlannerAction, { type: "move-many" }>): PlannerState {
    if (state.plan === null || action.placements.length === 0) {
        return state;
    }
    const map = getConfiguredPlanMap(state.plan);
    const movingIds = new Set(action.placements.map((placement) => placement.placementId));
    if (map === undefined || movingIds.size !== action.placements.length) {
        return { ...state, notice: "The selected component group is invalid." };
    }

    const sources = new Map(
        state.plan.placements
            .filter((placement) => movingIds.has(placement.id))
            .map((placement) => [placement.id, placement]),
    );
    if (sources.size !== movingIds.size) {
        return { ...state, notice: "One or more selected components can no longer be found." };
    }

    const provisional = state.plan.placements.filter((placement) => !movingIds.has(placement.id));
    const moves = new Map<string, Placement>();
    for (const target of action.placements) {
        const source = sources.get(target.placementId);
        if (source === undefined) {
            return { ...state, notice: "One or more selected components can no longer be found." };
        }
        const validation = validatePlacement(
            map,
            [...provisional, ...moves.values()],
            source.componentId,
            target.rackId,
            target.startU,
        );
        if (!validation.valid) {
            return { ...state, notice: validation.reason ?? "The selected group cannot be moved here." };
        }
        moves.set(source.id, { ...source, rackId: target.rackId, startU: target.startU });
    }

    const nextPlacements = state.plan.placements.map((placement) => moves.get(placement.id) ?? placement);
    return updatePlan(state, nextPlacements, `${moves.size} component${moves.size === 1 ? "" : "s"} moved.`);
}

function duplicateComponent(state: PlannerState, placementId: string): PlannerState {
    if (state.plan === null) {
        return state;
    }
    const source = state.plan.placements.find((item) => item.id === placementId);
    const map = getConfiguredPlanMap(state.plan);
    if (source === undefined || map === undefined) {
        return { ...state, notice: "The component can no longer be found." };
    }
    const startU = findFirstFreeStartU(map, state.plan.placements, source.componentId, source.rackId);
    if (startU === null) {
        return { ...state, notice: "There is no compatible free space in this rack." };
    }
    const copy: Placement = {
        ...source,
        id: createId("placement"),
        startU,
        ...(source.localName === undefined ? {} : { localName: `${source.localName} copy`.slice(0, 80) }),
    };
    return updatePlan(state, [...state.plan.placements, copy], `Component duplicated at U${startU}.`);
}

export function plannerReducer(state: PlannerState, action: PlannerAction): PlannerState {
    switch (action.type) {
        case "new-plan": {
            const now = new Date().toISOString();
            const map = getMap(action.mapId);
            const scenario = getScenario(action.scenarioId);
            if (
                map === undefined ||
                scenario === undefined ||
                !scenario.mapIds.includes(map.id) ||
                action.name.trim() === ""
            ) {
                return { ...state, notice: "Enter a plan name and select a supported map." };
            }
            const plan: Plan = {
                project: {
                    id: createId("project"),
                    name: action.name.trim().slice(0, 80),
                    mapId: action.mapId,
                    scenarioId: action.scenarioId,
                    createdAt: now,
                    updatedAt: now,
                },
                placements: [],
                rackSettings: [],
            };
            return commit(
                state,
                { plan, selectedRackId: map.racks[0]?.id ?? null, view: "floorplan" },
                "Plan created.",
            );
        }
        case "load-plan":
            return commit(
                state,
                {
                    plan: action.plan,
                    selectedRackId: getMap(action.plan.project.mapId)?.racks[0]?.id ?? null,
                    view: "floorplan",
                },
                action.notice ?? "Plan imported.",
            );
        case "rename-plan": {
            if (state.plan === null || action.name.trim() === "") {
                return { ...state, notice: "Plan name cannot be empty." };
            }
            const plan = {
                ...state.plan,
                project: {
                    ...state.plan.project,
                    name: action.name.trim().slice(0, 80),
                    updatedAt: new Date().toISOString(),
                },
            };
            return commit(state, { ...snapshot(state), plan }, "Plan renamed.");
        }
        case "set-scenario": {
            if (state.plan === null) {
                return { ...state, notice: "Create or import a plan first." };
            }
            const scenario = getScenario(action.scenarioId);
            if (scenario === undefined || !scenario.mapIds.includes(state.plan.project.mapId)) {
                return { ...state, notice: "The selected scenario is unavailable for this map." };
            }
            if (scenario.id === state.plan.project.scenarioId) {
                return state;
            }
            const plan: Plan = {
                ...state.plan,
                project: {
                    ...state.plan.project,
                    scenarioId: scenario.id,
                    updatedAt: new Date().toISOString(),
                },
            };
            return commit(state, { ...snapshot(state), plan }, "Scenario updated.");
        }
        case "configure-rack":
            return configureRack(state, action);
        case "reset-plan":
            return state.plan === null ? state : updatePlan(state, [], "All user placements were removed.");
        case "select-rack":
            return {
                ...state,
                selectedRackId: action.rackId,
                view: action.openDesigner === true ? "rack" : state.view,
            };
        case "set-view":
            return { ...state, view: action.view };
        case "place":
            return placeComponent(state, action);
        case "place-many":
            return placeComponents(state, action);
        case "move":
            return moveComponent(state, action);
        case "move-many":
            return moveComponents(state, action);
        case "rename-placement": {
            if (state.plan === null || action.localName.length > 80) {
                return { ...state, notice: "Local names can contain up to 80 characters." };
            }
            const localName = action.localName.trim();
            const placements = state.plan.placements.map((item) => {
                if (item.id !== action.placementId) {
                    return item;
                }
                const { localName: _discarded, ...base } = item;
                return localName === "" ? base : { ...base, localName };
            });
            return updatePlan(state, placements, localName === "" ? "Local name removed." : "Local name updated.");
        }
        case "duplicate":
            return duplicateComponent(state, action.placementId);
        case "delete":
            return state.plan === null
                ? state
                : updatePlan(
                      state,
                      state.plan.placements.filter((placement) => placement.id !== action.placementId),
                      "Component removed.",
                  );
        case "delete-many": {
            if (state.plan === null || action.placementIds.length === 0) {
                return state;
            }
            const placementIds = new Set(action.placementIds);
            if (placementIds.size !== action.placementIds.length) {
                return { ...state, notice: "The selected component group is invalid." };
            }
            const placements = state.plan.placements.filter((placement) => !placementIds.has(placement.id));
            const removedCount = state.plan.placements.length - placements.length;
            if (removedCount !== placementIds.size) {
                return { ...state, notice: "One or more selected components can no longer be found." };
            }
            return updatePlan(state, placements, `${removedCount} component${removedCount === 1 ? "" : "s"} removed.`);
        }
        case "undo": {
            const previous = state.past.at(-1);
            if (previous === undefined) {
                return { ...state, notice: "Nothing to undo." };
            }
            return {
                ...state,
                ...previous,
                past: state.past.slice(0, -1),
                future: [snapshot(state), ...state.future].slice(0, HISTORY_LIMIT),
                notice: "Change undone.",
            };
        }
        case "redo": {
            const next = state.future[0];
            if (next === undefined) {
                return { ...state, notice: "Nothing to redo." };
            }
            return {
                ...state,
                ...next,
                past: [...state.past, snapshot(state)].slice(-HISTORY_LIMIT),
                future: state.future.slice(1),
                notice: "Change restored.",
            };
        }
        case "mark-exported":
            return {
                ...state,
                plan:
                    state.plan === null
                        ? null
                        : { ...state.plan, project: { ...state.plan.project, updatedAt: action.updatedAt } },
                exportedUpdatedAt: action.updatedAt,
                notice: "Portable .utp file exported.",
            };
        case "notice":
            return { ...state, notice: action.notice };
    }
}
